import { useEffect, useState } from "react";
import { PackagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMaster } from "@/lib/useMaster";
import { formatDateTime, formatIDR } from "@/lib/format";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const selectCls = "h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600";

export default function StokMasuk() {
  const { user } = useAuth();
  const { products, warehouses, productMap, warehouseMap } = useMaster();
  const [batches, setBatches] = useState([]);
  const [batchWhFilter, setBatchWhFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const [supplier, setSupplier] = useState("");
  const [items, setItems] = useState([{ product_id: "", qty: "", purchase_price: "" }]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ qty: "", purchase_price: "", supplier: "" });

  const loadBatches = () => {
    const params = batchWhFilter ? `?warehouse_id=${batchWhFilter}` : "";
    api.get(`/stock/batches${params}`).then((r) => setBatches(r.data)).catch(() => {});
  };

  useEffect(loadBatches, [batchWhFilter]);

  const openAdd = () => {
    setWarehouseId(warehouses[0]?.id || "");
    setSupplier("");
    setItems([{ product_id: "", qty: "", purchase_price: "" }]);
    setAddOpen(true);
  };

  const setItem = (i, patch) => {
    setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };

  const submit = async (e) => {
    e.preventDefault();
    const validItems = items.filter((it) => it.product_id && parseInt(it.qty, 10) > 0 && it.purchase_price !== "");
    if (validItems.length === 0) {
      toast.error("Add at least one item with product, quantity, and price");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/stock/in-multi", {
        warehouse_id: warehouseId,
        supplier,
        items: validItems.map((it) => ({ product_id: it.product_id, qty: parseInt(it.qty, 10), purchase_price: parseFloat(it.purchase_price) || 0 })),
      });
      toast.success(`${data.created} purchase batch${data.created > 1 ? "es" : ""} recorded (FIFO)`);
      setAddOpen(false);
      loadBatches();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const removeBatch = async (b) => {
    if (!window.confirm(`Delete batch ${b.batch_no}? The batch is voided and removed from stock, but kept in history for audit.`)) return;
    try {
      await api.delete(`/stock/batches/${b.id}`);
      toast.success("Purchase batch deleted");
      loadBatches();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const openEdit = (b) => {
    setEditing(b);
    setEditForm({ qty: String(b.qty_initial), purchase_price: String(b.purchase_price), supplier: b.supplier || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/stock/batches/${editing.id}`, {
        qty: parseInt(editForm.qty, 10),
        purchase_price: parseFloat(editForm.purchase_price),
        supplier: editForm.supplier,
      });
      toast.success(`Batch ${editing.batch_no} updated`);
      setEditing(null);
      loadBatches();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="stok-masuk-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Stock In</h1>
          <p className="mt-1 text-sm text-zinc-500">Record purchases as batches — the basis for FIFO costing.</p>
        </div>
        <Button data-testid="btn-add-batch" onClick={openAdd} className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> Add Batch
        </Button>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>New Purchase Batch</DialogTitle>
            <DialogDescription className="sr-only">Record a purchase with one or more products</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Destination Warehouse</Label>
                <select data-testid="stockin-warehouse" required value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className={selectCls}>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Input data-testid="stockin-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="PT Distributor Resmi" className="rounded-sm border-zinc-300" />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Items</Label>
                <button type="button" data-testid="stockin-add-item" onClick={() => setItems([...items, { product_id: "", qty: "", purchase_price: "" }])} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                  + Add Item
                </button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`stockin-item-row-${i}`}>
                  <div className="flex-1">
                    <SearchableSelect
                      testid={`stockin-item-product-${i}`}
                      options={products.map((p) => ({ value: p.id, label: `${p.name} — ${p.sku}` }))}
                      value={it.product_id}
                      onChange={(v) => setItem(i, { product_id: v })}
                      placeholder="Select product..."
                    />
                  </div>
                  <Input data-testid={`stockin-item-qty-${i}`} type="number" min="1" required value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} placeholder="Qty" className="w-24 rounded-sm border-zinc-300" />
                  <Input data-testid={`stockin-item-price-${i}`} type="number" min="0" required value={it.purchase_price} onChange={(e) => setItem(i, { purchase_price: e.target.value })} placeholder="Price / unit" className="w-36 rounded-sm border-zinc-300" />
                  {items.length > 1 && (
                    <button type="button" data-testid={`stockin-item-remove-${i}`} onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="mt-2 rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <p className="text-xs text-zinc-400">Each product becomes its own FIFO batch under the same warehouse & supplier.</p>
            </div>

            <Button data-testid="stockin-submit" type="submit" disabled={saving} className="w-full rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
              <PackagePlus className="mr-2 h-4 w-4" /> {saving ? "Saving..." : `Save ${items.filter((it) => it.product_id).length || ""} Batch${items.filter((it) => it.product_id).length > 1 ? "es" : ""}`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Batch {editing?.batch_no}</DialogTitle>
            <DialogDescription className="sr-only">Edit purchase batch quantity, price, and supplier</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input data-testid="edit-batch-qty" type="number" min="1" required value={editForm.qty} onChange={(e) => setEditForm({ ...editForm, qty: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
              <div className="space-y-2">
                <Label>Purchase Price / Unit</Label>
                <Input data-testid="edit-batch-price" type="number" min="0" required value={editForm.purchase_price} onChange={(e) => setEditForm({ ...editForm, purchase_price: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Input data-testid="edit-batch-supplier" value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} className="rounded-sm border-zinc-300" />
            </div>
            <p className="text-xs text-zinc-400">Only unused purchase batches can be edited. The stock ledger entry is updated automatically.</p>
            <Button data-testid="edit-batch-submit" type="submit" disabled={saving} className="w-full rounded-sm bg-blue-600 text-white hover:bg-blue-700">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <div className="flex items-center gap-3 border-b border-zinc-200 px-4 py-3">
          <span className="text-xs font-medium text-zinc-500">Warehouse:</span>
          <select data-testid="stockin-warehouse-filter" value={batchWhFilter} onChange={(e) => setBatchWhFilter(e.target.value)} className="h-9 w-56 rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
            <option value="">All Warehouses</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
            ))}
          </select>
          {batchWhFilter && (
            <button type="button" data-testid="stockin-warehouse-filter-clear" onClick={() => setBatchWhFilter("")} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
              Clear
            </button>
          )}
        </div>
        <table className="w-full text-sm" data-testid="batch-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-medium">Batch No.</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Product</th>
              <th className="px-4 py-3 font-medium">Warehouse</th>
              <th className="px-4 py-3 text-right font-medium">Initial Qty</th>
              <th className="px-4 py-3 text-right font-medium">Remaining</th>
              <th className="px-4 py-3 text-right font-medium">Purchase Price</th>
              <th className="px-4 py-3 font-medium">Supplier</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 text-right font-medium sticky right-0 bg-zinc-50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-zinc-400" data-testid="batch-empty">
                  No purchase batches yet
                </td>
              </tr>
            )}
            {batches.map((b) => (
              <tr key={b.id} className={`border-b border-zinc-100 last:border-0 hover:bg-zinc-50 ${b.deleted ? "opacity-50" : ""}`} data-testid={`batch-row-${b.batch_no}`}>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">
                  {b.batch_no}
                  <span className="block font-sans text-[10px] text-zinc-400">by {b.created_by_name || "-"}</span>
                  {b.deleted && <span className="block font-sans text-[10px] font-semibold text-red-500" data-testid={`batch-deleted-label-${b.batch_no}`}>deleted by {b.deleted_by_name}</span>}
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{formatDateTime(b.created_at)}</td>
                <td className="px-4 py-2.5 font-medium text-zinc-900">{productMap[b.product_id]?.name || "?"}</td>
                <td className="px-4 py-2.5 text-zinc-500">{warehouseMap[b.warehouse_id]?.name || "?"}</td>
                <td className="tnum px-4 py-2.5 text-right text-zinc-700">{b.qty_initial}</td>
                <td className={`tnum px-4 py-2.5 text-right font-semibold ${b.qty_remaining === 0 ? "text-zinc-400" : "text-emerald-600"}`}>{b.qty_remaining}</td>
                <td className="tnum px-4 py-2.5 text-right text-zinc-700">{formatIDR(b.purchase_price)}</td>
                <td className="px-4 py-2.5 text-zinc-500">{b.supplier || "-"}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${b.source === "transfer" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-zinc-200 bg-zinc-50 text-zinc-600"}`}>
                    {b.source === "transfer" ? "Transfer" : "Purchase"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right sticky right-0 bg-white">
                  {!b.deleted && b.source === "purchase" && b.qty_remaining === b.qty_initial && user?.role === "owner" && (
                    <button data-testid={`btn-edit-batch-${b.batch_no}`} onClick={() => openEdit(b)} className="rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-blue-50 hover:text-blue-600" title="Edit batch">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {!b.deleted && b.source === "purchase" && b.qty_remaining === b.qty_initial && (
                    <button data-testid={`btn-delete-batch-${b.batch_no}`} onClick={() => removeBatch(b)} className="rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete batch">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
